import SalaryStructureManager from '../employee-management/salary-structure/SalaryStructureManager.jsx';
import StatutoryRatesManager from '../employee-management/salary-structure/StatutoryRatesManager.jsx';
import TdsReferenceHeadsManager from '../employee-management/salary-structure/TdsReferenceHeadsManager.jsx';
import { useState, useEffect, useMemo } from 'react';
import { formatDate } from '../../../lib/employeeHelpers';
import { toast } from 'react-hot-toast';
import { X } from 'lucide-react';
import {
  loadStatutoryRatesFromStorage,
  STATUTORY_RATES_CHANGED_EVENT,
  applyStatutoryConfigJson,
} from '../employee-management/salary-structure/utils/statutoryRatesDefaults';
import {
  findStructureComponentsMissingFromLibrary,
  mapApiToLibrary,
  loadMergedFormulasFromStorage,
  persistFormulasToStorage,
} from '../employee-management/salary-structure/utils/salaryComponentLibrarySync';
import { salaryStructureService } from '../../../services/api';
import { usePermissions } from '../../../rbac/PermissionsContext';
import { DsButton } from '../../../design-system';
import { resolveCompanyMinDateValue } from '../../../utils/selectableDateBounds';


// Enhanced Salary Structure Manager Component - Local Implementation
const EnhancedSalaryStructureManager = ({
  darkMode,
  employees,
  departmentsList = [],
  branchesList = [],
  designationsList = [],
  subDepartmentsList = [],
  gradesList = [],
  categoriesList = [],
  /** "full" = all tabs; "structuresOnly" = Salary Structures panel without inner tab strip */
  variant = "full",
}) => {
    const { hasScreenAccess } = usePermissions() || {};
    const canViewTdsHeads =
      typeof hasScreenAccess !== "function" ||
      hasScreenAccess("payroll", "tds_reference_heads", "view") ||
      hasScreenAccess("payroll", "salary_structure", "view");
    const structuresOnly = variant === "structuresOnly";
    const companyMinDate = useMemo(() => resolveCompanyMinDateValue() || undefined, []);
    // Core state for the new salary structure system
    const [activeTab, setActiveTab] = useState('structures');
    const [statutoryRates, setStatutoryRates] = useState(() => loadStatutoryRatesFromStorage());
    const [statutoryConfigJson, setStatutoryConfigJson] = useState(null);
    const [templates, setTemplates] = useState([]);
    const [components, setComponents] = useState([]);
    const [statutoryConfigs, setStatutoryConfigs] = useState([]);
    const [structures, setStructures] = useState([]);
    const [versions, setVersions] = useState([]);
    const [scenarios, setScenarios] = useState([]);
    const [selectedTemplate, setSelectedTemplate] = useState(null);
    const [selectedStructure, setSelectedStructure] = useState(null);
    const [showFormulaBuilder, setShowFormulaBuilder] = useState(false);
    const [showStatutoryModal, setShowStatutoryModal] = useState(false);
    const [selectedComponent, setSelectedComponent] = useState(null);
    const [selectedStatutoryConfig, setSelectedStatutoryConfig] = useState(null);
    const [openDropdownId, setOpenDropdownId] = useState(null);
    const [showFormulaManager, setShowFormulaManager] = useState(false);
    const [savedFormulas, setSavedFormulas] = useState([]);
    const [selectedFormula, setSelectedFormula] = useState(null);
    const [verificationResult, setVerificationResult] = useState(null);
    const [showFormulaSelection, setShowFormulaSelection] = useState(false);
    const [selectedFormulaForComponent, setSelectedFormulaForComponent] = useState(null);
    const [complianceReport, setComplianceReport] = useState([]);

    useEffect(() => {
      if (activeTab === 'simulator') setActiveTab('structures');
    }, [activeTab]);

    useEffect(() => {
      let cancelled = false;
      (async () => {
        try {
          const res = await salaryStructureService.getStatutoryConfig();
          if (cancelled) return;
          const row = res?.data;
          if (row?.configJson) {
            setStatutoryConfigJson(row.configJson);
            const applied = applyStatutoryConfigJson(
              row.configJson,
              loadStatutoryRatesFromStorage()
            );
            if (applied.rates) setStatutoryRates(applied.rates);
          }
        } catch (err) {
          console.error('Failed to load statutory config for structures', err);
        }
      })();
      return () => {
        cancelled = true;
      };
    }, []);

    useEffect(() => {
      const onRates = (e) => {
        if (e?.detail) setStatutoryRates(e.detail);
        else setStatutoryRates(loadStatutoryRatesFromStorage());
      };
      window.addEventListener(STATUTORY_RATES_CHANGED_EVENT, onRates);
      return () => window.removeEventListener(STATUTORY_RATES_CHANGED_EVENT, onRates);
    }, []);

    useEffect(() => {
      const reloadConfig = async () => {
        try {
          const res = await salaryStructureService.getStatutoryConfig();
          if (res?.data?.configJson) {
            setStatutoryConfigJson(res.data.configJson);
          }
        } catch {
          /* keep last loaded config */
        }
      };
      window.addEventListener(STATUTORY_RATES_CHANGED_EVENT, reloadConfig);
      return () => window.removeEventListener(STATUTORY_RATES_CHANGED_EVENT, reloadConfig);
    }, []);
  
    // Load salary components from API (migrate any localStorage leftovers once)
    useEffect(() => {
      let cancelled = false;
      (async () => {
        try {
          const res = await salaryStructureService.listComponents();
          if (cancelled) return;
          const apiRows = (Array.isArray(res?.data) ? res.data : []).map((row) => ({
            id: row.id,
            name: row.name || "",
            code: row.code || "",
            category:
              row.category === "EMPLOYER_BENEFIT" ? "BENEFIT" : row.category || "EARNING",
            notes: row.notes || "",
            sortOrder: row.sortOrder ?? 0,
            isActive: row.active !== false,
          }));

          // One-time migrate browser-local components not yet in DB
          const localRaw = localStorage.getItem("salaryComponents");
          const localRows = localRaw ? JSON.parse(localRaw) : [];
          const apiCodes = new Set(apiRows.map((c) => String(c.code || "").toUpperCase()));
          const toMigrate = (Array.isArray(localRows) ? localRows : []).filter((c) => {
            const code = String(c.code || c.name || "")
              .toUpperCase()
              .replace(/\s+/g, "_");
            return code && !apiCodes.has(code);
          });

          let merged = apiRows;
          if (toMigrate.length) {
            for (const c of toMigrate) {
              try {
                const code = String(c.code || c.name || "")
                  .toUpperCase()
                  .replace(/\s+/g, "_");
                const sug = await salaryStructureService.suggestTdsReferenceHead({
                  name: c.name || code,
                  code,
                  category: c.category || "EARNING",
                });
                const tdsReferenceHeadId = sug?.data?.head?.id || null;
                if (!tdsReferenceHeadId) {
                  console.warn("Skip migrating local component (no TDS head)", c?.name);
                  continue;
                }
                const created = await salaryStructureService.createComponent({
                  code,
                  name: c.name || code,
                  category: c.category || "EARNING",
                  notes: c.notes || null,
                  sortOrder: c.sortOrder ?? 0,
                  active: c.isActive !== false,
                  nature: "FIXED",
                  calculationType: "FIXED",
                  tdsReferenceHeadId,
                });
                const row = created?.data;
                if (row) {
                  merged = [
                    ...merged,
                    {
                      id: row.id,
                      name: row.name || "",
                      code: row.code || "",
                      category:
                        row.category === "EMPLOYER_BENEFIT"
                          ? "BENEFIT"
                          : row.category || "EARNING",
                      notes: row.notes || "",
                      sortOrder: row.sortOrder ?? 0,
                      isActive: row.active !== false,
                    },
                  ];
                }
              } catch (migErr) {
                console.warn("Skip migrating local component", c?.name, migErr);
              }
            }
            localStorage.removeItem("salaryComponents");
          }

          setComponents(merged);
        } catch (err) {
          console.error("Failed to load salary components from API", err);
          // Fallback to localStorage so UI is not empty offline
          try {
            const saved = localStorage.getItem("salaryComponents");
            if (saved) setComponents(JSON.parse(saved));
          } catch {
            /* ignore */
          }
          toast.error(
            err?.response?.data?.message ||
              "Could not load salary components from server"
          );
        }
      })();
      return () => {
        cancelled = true;
      };
    }, []);

    // Load other legacy localStorage slices (structures now come from API via SalaryStructureManager)
    useEffect(() => {
      const savedTemplates = localStorage.getItem('salaryTemplates');
      const savedStatutoryConfigs = localStorage.getItem('salaryStatutoryConfigs');
      const savedStructures = localStorage.getItem('salaryStructures');
      const savedVersions = localStorage.getItem('salaryVersions');
      const savedScenarios = localStorage.getItem('salaryScenarios');
      const savedFormulas =
        localStorage.getItem('salaryFormulas') ||
        localStorage.getItem('savedFormulas');
      
      if (savedTemplates) setTemplates(JSON.parse(savedTemplates));
      if (savedStatutoryConfigs) setStatutoryConfigs(JSON.parse(savedStatutoryConfigs));
      if (savedStructures) setStructures(JSON.parse(savedStructures));
      if (savedVersions) setVersions(JSON.parse(savedVersions));
      if (savedScenarios) setScenarios(JSON.parse(savedScenarios));
      if (savedFormulas) {
        const parsed = loadMergedFormulasFromStorage();
        setSavedFormulas(parsed);
        persistFormulasToStorage(parsed);
      } else {
        const merged = loadMergedFormulasFromStorage();
        if (merged.length) {
          setSavedFormulas(merged);
          persistFormulasToStorage(merged);
        }
      }
    }, []);
  
    // Handle click outside for dropdown
    useEffect(() => {
      const handleClickOutside = (event) => {
        if (openDropdownId && !event.target.closest('.component-dropdown')) {
          setOpenDropdownId(null);
        }
      };
  
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }, [openDropdownId]);
  
    // Save functions for localStorage
    const saveTemplates = (newTemplates) => {
      setTemplates(newTemplates);
      localStorage.setItem('salaryTemplates', JSON.stringify(newTemplates));
    };
  
    const mapApiComponent = mapApiToLibrary;

    const refetchComponents = async () => {
      const res = await salaryStructureService.listComponents();
      const rows = (Array.isArray(res?.data) ? res.data : []).map(mapApiComponent);
      setComponents(rows);
      return rows;
    };

    const saveComponents = (newComponents) => {
      setComponents(newComponents);
    };

    const createComponentFromPanel = async (draft) => {
      const code = String(draft.code || draft.name || "")
        .toUpperCase()
        .replace(/\s+/g, "_");
      let tdsReferenceHeadId = draft.tdsReferenceHeadId;
      if (!tdsReferenceHeadId) {
        try {
          const sug = await salaryStructureService.suggestTdsReferenceHead({
            name: draft.name || code,
            code,
            category: draft.category || "EARNING",
          });
          tdsReferenceHeadId = sug?.data?.head?.id || null;
        } catch {
          /* require explicit map if suggest fails */
        }
      }
      if (!tdsReferenceHeadId) {
        throw new Error(
          `Map TDS Reference Head for component "${draft.name || code}" before saving`
        );
      }
      const res = await salaryStructureService.createComponent({
        code,
        name: draft.name || code,
        category: draft.category || "EARNING",
        notes: draft.notes || null,
        sortOrder: draft.sortOrder ?? 0,
        active: draft.isActive !== false,
        nature: "FIXED",
        calculationType: "FIXED",
        tdsReferenceHeadId,
        flexiEnabled: !!draft.flexiEnabled,
        flexiConfigJson: draft.flexiConfigJson || null,
      });
      const row = res?.data;
      if (row) {
        setComponents((prev) => {
          if (prev.some((c) => String(c.id) === String(row.id))) return prev;
          return [...prev, mapApiComponent(row)];
        });
        return mapApiComponent(row);
      }
      await refetchComponents();
      return null;
    };

    /** Ensure structure-embedded components (Telephone, Fuel, etc.) exist in library */
    const syncLibraryFromStructures = async (structureList) => {
      let latest = components;
      try {
        latest = await refetchComponents();
      } catch {
        /* keep current state */
      }
      const missing = findStructureComponentsMissingFromLibrary(
        structureList || [],
        latest || []
      );
      if (!missing.length) return latest;
      for (const draft of missing) {
        try {
          await createComponentFromPanel(draft);
        } catch (err) {
          // Already in library (duplicate code) — continue with remaining
          if (err?.response?.status !== 409) {
            console.warn("Failed to sync structure component into library", draft?.code, err);
          }
        }
      }
      return refetchComponents();
    };

    // Create component (legacy formula-builder path)
    const createComponent = (componentData) => {
      const newComponent = {
        id: Date.now(),
        code: '',
        name: '',
        category: 'EARNING',
        ...componentData,
        nature: 'FIXED',
        calculationType: 'FIXED',
        valueExpression: '',
        frequency: 'MONTHLY',
        flags: {
          pfApplicable: false,
          esiApplicable: false,
          taxable: 'TAXABLE'
        },
        notes: '',
        isActive: true
      };
      createComponentFromPanel(newComponent).catch((err) => {
        toast.error(err?.response?.data?.message || 'Failed to create component');
      });
    };
  
    // Update component
    const updateComponent = async (id, updates) => {
      const current = components.find((c) => c.id === id) || {};
      const merged = { ...current, ...updates };
      const res = await salaryStructureService.updateComponent(id, {
        code: merged.code,
        name: merged.name,
        category: merged.category || "EARNING",
        notes: merged.notes || null,
        sortOrder: merged.sortOrder ?? 0,
        active: merged.isActive !== false,
        tdsReferenceHeadId: merged.tdsReferenceHeadId,
        flexiEnabled: !!merged.flexiEnabled,
        flexiConfigJson: merged.flexiConfigJson || null,
      });
      const row = res?.data;
      if (row) {
        setComponents((prev) =>
          prev.map((c) => (c.id === id ? mapApiComponent(row) : c))
        );
      } else {
        await refetchComponents();
      }
    };
  
    // Delete component
    const deleteComponent = async (id) => {
      await salaryStructureService.deleteComponent(id);
      setComponents((prev) => prev.filter((comp) => comp.id !== id));
    };
  
    const saveStatutoryConfigs = (newConfigs) => {
      setStatutoryConfigs(newConfigs);
      localStorage.setItem('salaryStatutoryConfigs', JSON.stringify(newConfigs));
    };
  
    const saveStructures = (newStructures) => {
      setStructures(newStructures);
      localStorage.setItem('salaryStructures', JSON.stringify(newStructures));
    };
  
    const saveVersions = (newVersions) => {
      setVersions(newVersions);
      localStorage.setItem('salaryVersions', JSON.stringify(newVersions));
    };
  
    const saveScenarios = (newScenarios) => {
      setScenarios(newScenarios);
      localStorage.setItem('salaryScenarios', JSON.stringify(newScenarios));
    };
  
    const saveFormulas = (newFormulas) => {
      setSavedFormulas(newFormulas);
      persistFormulasToStorage(newFormulas);
    };
  
    // Add new formula
    const addFormula = (formulaData) => {
      const newFormula = {
        id: Date.now(),
        name: formulaData.name,
        code: formulaData.code,
        formula: formulaData.formula,
        componentId: formulaData.componentId,
        category: formulaData.category || 'CUSTOM'
      };
      saveFormulas([...savedFormulas, newFormula]);
    };
  
    // Update formula
    const updateFormula = (id, updates) => {
      const updatedFormulas = savedFormulas.map(formula => 
        formula.id === id ? { ...formula, ...updates } : formula
      );
      saveFormulas(updatedFormulas);
    };
  
    // Delete formula
    const deleteFormula = (id) => {
      const filteredFormulas = savedFormulas.filter(formula => formula.id !== id);
      saveFormulas(filteredFormulas);
    };
  
    // Formula Manager helper functions
    const addToFormula = (text) => {
      setSelectedFormula(prev => ({
        ...prev,
        formula: (prev?.formula || '') + text
      }));
    };
  
    const verifyFormula = () => {
      try {
        const formula = selectedFormula?.formula || '';
        if (!formula.trim()) {
          setVerificationResult({ valid: false, error: 'Formula cannot be empty' });
          return;
        }
  
        // Create a safe evaluation environment
        const safeEval = (expr) => {
          // Replace component names with test values
          let testFormula = formula;
          const testValues = {
            'BASIC': 50000,
            'DA': 2500,
            'HRA': 20000,
            'GROSS_EARNINGS': 72500,
            'CTC': 100000,
            'PF_EMP': 6000,
            'PF_EMPR': 6000,
            'ESI_EMP': 543,
            'ESI_EMPR': 2356
          };
  
          // Add component test values
          components.forEach(comp => {
            testValues[comp.code || comp.name] = 1000; // Default test value
          });
  
          // Replace all variables with test values
          Object.keys(testValues).forEach(key => {
            const regex = new RegExp(`\\b${key}\\b`, 'g');
            testFormula = testFormula.replace(regex, testValues[key]);
          });
  
          // Basic validation - only allow safe operations
          const allowedChars = /^[0-9+\-*/().\s]+$/;
          if (!allowedChars.test(testFormula)) {
            throw new Error('Formula contains invalid characters');
          }
  
          // Evaluate the formula
          const result = eval(testFormula);
          return isFinite(result) ? result : 0;
        };
  
        const result = safeEval(formula);
        setVerificationResult({ 
          valid: true, 
          result: result,
          message: `Formula is valid. Test result: ₹${result.toFixed(2)}`
        });
      } catch (error) {
        setVerificationResult({ 
          valid: false, 
          error: `Formula error: ${error.message}` 
        });
      }
    };
  
    const saveFormula = () => {
      if (!selectedFormula?.name || !selectedFormula?.formula) {
        toast.error('Please provide both name and formula');
        return;
      }
  
      if (!verificationResult?.valid) {
        toast.error('Please verify the formula before saving');
        return;
      }
  
      if (selectedFormula.id) {
        // Update existing formula
        updateFormula(selectedFormula.id, {
          name: selectedFormula.name,
          code: selectedFormula.code,
          formula: selectedFormula.formula
        });
      } else {
        // Add new formula
        addFormula({
          name: selectedFormula.name,
          code: selectedFormula.code,
          formula: selectedFormula.formula,
          componentId: null,
          category: 'CUSTOM'
        });
      }
  
      setSelectedFormula(null);
      setVerificationResult(null);
      toast.success('Formula saved successfully!');
    };
  
    // Add new component with enhanced structure
    const addComponent = () => {
      const newComponent = {
        id: Date.now(),
        name: '',
        code: '',
        category: 'EARNING', // EARNING, DEDUCTION, EMPLOYER_BENEFIT
        nature: 'FIXED', // FIXED, VARIABLE, STATUTORY, PERQUISITE, ONE_TIME
        calculationType: 'FIXED', // FIXED, PERCENT_OF_BASIC, PERCENT_OF_GROSS, PERCENT_OF_CTC, FORMULA, SLAB, PRO_RATA, BALANCING
        valueExpression: '',
        frequency: 'MONTHLY', // MONTHLY, ANNUAL, QUARTERLY
        flags: {
          pfApplicable: false,
          esiApplicable: false,
          taxable: 'TAXABLE' // TAXABLE, EXEMPT, PARTIAL
        },
        notes: '',
        isActive: true
      };
      saveComponents([...components, newComponent]);
      
      // Add component to formula manager
      const newFormula = {
        id: Date.now(),
        name: newComponent.name || 'Unnamed Component',
        code: newComponent.code || '',
        formula: newComponent.valueExpression || '',
        componentId: newComponent.id,
        category: 'COMPONENT'
      };
      saveFormulas([...savedFormulas, newFormula]);
    };
  
    // Add new template
    const addTemplate = () => {
      const newTemplate = {
        id: Date.now(),
        name: 'New Template',
        mode: 'CTC', // CTC, GROSS
        components: [],
        statutoryConfigId: null,
        defaultCityType: 'METRO', // METRO, NON_METRO
        active: true
      };
      saveTemplates([...templates, newTemplate]);
    };
  
    // Update template
    const updateTemplate = (id, updates) => {
      const updatedTemplates = templates.map(template => 
        template.id === id ? { ...template, ...updates } : template
      );
      saveTemplates(updatedTemplates);
    };
  
    // Delete template
    const deleteTemplate = (id) => {
      const filteredTemplates = templates.filter(template => template.id !== id);
      saveTemplates(filteredTemplates);
    };
  
    // Add new statutory config
    const addStatutoryConfig = () => {
      const newConfig = {
        id: Date.now(),
        name: 'Default Config',
        pf: {
          employeePct: 12,
          employerPct: 12,
          wageCeiling: 15000,
          applyCeiling: true,
          voluntaryAboveCeiling: false,
          wageDefinition: 'BASIC_DA'
        },
        esi: {
          employeePct: 0.75,
          employerPct: 3.25,
          eligibilityGrossMonthly: 21000
        },
        gratuity: {
          basis: 'BASIC',
          ratePct: 4.81
        },
        bonus: {
          eligibilityMonthlyWage: 21000,
          minPct: 8.33,
          maxPct: 20,
          wageDefinition: 'STATUTORY_WAGE'
        },
        pt: {
          state: '',
          slabs: {}
        },
        lwf: {
          state: '',
          employeeAmt: 0,
          employerAmt: 0,
          periodicity: 'MONTHLY'
        },
        labourCodeRule: {
          minBasicPlusDAFractionOfWages: 0.5
        },
        minimumWage: {
          enabled: false,
          tableRef: null
        }
      };
      saveStatutoryConfigs([...statutoryConfigs, newConfig]);
    };
  
    // Update statutory config
    const updateStatutoryConfig = (id, updates) => {
      const updatedConfigs = statutoryConfigs.map(config => 
        config.id === id ? { ...config, ...updates } : config
      );
      saveStatutoryConfigs(updatedConfigs);
    };
  
    // Delete statutory config
    const deleteStatutoryConfig = (id) => {
      const filteredConfigs = statutoryConfigs.filter(config => config.id !== id);
      saveStatutoryConfigs(filteredConfigs);
    };
  
    // Add new structure
    const addStructure = () => {
      const newStructure = {
        id: Date.now(),
        templateId: null,
        title: '',
        effectiveFrom: '',
        mode: 'CTC', // CTC, GROSS
        targetAnnual: 0,
        parameters: {
          cityType: 'METRO',
          payableDaysMonthly: null
        },
        resolvedComponents: [],
        computedSummary: {
          monthlyGross: 0,
          annualGross: 0,
          employerContrib: 0,
          annualCTC: 0,
          notes: []
        },
        complianceReport: [],
        versionInfoId: null
      };
      saveStructures([...structures, newStructure]);
    };
  
    // Update structure
    const updateStructure = (id, updates) => {
      const updatedStructures = structures.map(structure => 
        structure.id === id ? { ...structure, ...updates } : structure
      );
      saveStructures(updatedStructures);
    };
  
    // Delete structure
    const deleteStructure = (id) => {
      const filteredStructures = structures.filter(structure => structure.id !== id);
      saveStructures(filteredStructures);
    };
  
    // Enhanced calculation engine
    const calculateSalary = (structure, targetAnnual, parameters = {}, statutoryConfig = null) => {
      try {
        // Sort components by calculation order
        const sortedComponents = [...structure.resolvedComponents].sort((a, b) => a.ordering - b.ordering);
        
        let basic = 0;
        let da = 0;
        let hra = 0;
        let grossEarnings = 0;
        let netEarnings = 0;
        let employerBenefits = 0;
        
        // Calculate components in order
        sortedComponents.forEach(component => {
          let value = 0;
          
          switch (component.calculationType) {
            case 'FIXED':
              value = parseFloat(component.valueExpression) || 0;
              break;
            case 'PERCENT_OF_BASIC':
              value = (basic * parseFloat(component.valueExpression)) / 100;
              break;
            case 'PERCENT_OF_GROSS':
              value = (grossEarnings * parseFloat(component.valueExpression)) / 100;
              break;
            case 'PERCENT_OF_CTC':
              value = (targetAnnual * parseFloat(component.valueExpression)) / 100;
              break;
            case 'FORMULA':
              try {
                // Replace tokens with actual values
                let formula = component.valueExpression;
                formula = formula.replace(/BASIC/g, basic);
                formula = formula.replace(/DA/g, da);
                formula = formula.replace(/HRA/g, hra);
                formula = formula.replace(/GROSS_EARNINGS/g, grossEarnings);
                formula = formula.replace(/CTC/g, targetAnnual);
                value = eval(formula);
              } catch (error) {
                console.error('Formula evaluation error:', error);
                value = 0;
              }
              break;
            case 'BALANCING':
              // Calculate balancing amount
              if (structure.mode === 'CTC') {
                value = targetAnnual - grossEarnings - employerBenefits;
              } else {
                value = targetAnnual - grossEarnings;
              }
              break;
            default:
              value = 0;
          }
          
          // Apply pro-rata if applicable
          if (parameters.payableDaysMonthly && component.flags?.proratable) {
            value = (value * parameters.payableDaysMonthly) / 30;
          }
          
          // Update running totals
          if (component.category === 'EARNING') {
            grossEarnings += value;
            if (component.code === 'BASIC') basic = value;
            if (component.code === 'DA') da = value;
            if (component.code === 'HRA') hra = value;
          } else if (component.category === 'DEDUCTION') {
            netEarnings = grossEarnings - value;
          } else if (component.category === 'EMPLOYER_BENEFIT') {
            employerBenefits += value;
          }
          
          // Update component with calculated value
          component.calculatedValue = value;
        });
        
        // Calculate statutory components if config provided
        let statutory = {};
        if (statutoryConfig) {
          // PF calculation
          const pfWage = basic + da;
          const pfCeiling = statutoryConfig.pf.wageCeiling;
          const pfWageForCalc = Math.min(pfWage, pfCeiling);
          statutory.pfEmployee = (pfWageForCalc * statutoryConfig.pf.employeePct) / 100;
          statutory.pfEmployer = (pfWageForCalc * statutoryConfig.pf.employerPct) / 100;
          
          // ESI calculation
          if (grossEarnings <= statutoryConfig.esi.eligibilityGrossMonthly) {
            statutory.esiEmployee = (grossEarnings * statutoryConfig.esi.employeePct) / 100;
            statutory.esiEmployer = (grossEarnings * statutoryConfig.esi.employerPct) / 100;
          }
          
          // Gratuity calculation (annualized)
          statutory.gratuity = (basic * statutoryConfig.gratuity.ratePct * 12) / 100;
        }
        
        return {
          summary: {
            monthlyGross: grossEarnings,
            annualGross: grossEarnings * 12,
            employerContrib: employerBenefits + (statutory.pfEmployer || 0) + (statutory.esiEmployer || 0) + (statutory.gratuity || 0),
            annualCTC: targetAnnual,
            netSalary: netEarnings,
            notes: []
          },
          statutory,
          components: sortedComponents
        };
      } catch (error) {
        console.error('Salary calculation error:', error);
        return {
          summary: {
            monthlyGross: 0,
            annualGross: 0,
            employerContrib: 0,
            annualCTC: targetAnnual,
            netSalary: 0,
            notes: ['Calculation error occurred']
          },
          statutory: {},
          components: []
        };
      }
    };
  
    // Compliance check function
    const runComplianceCheck = (structure, calculatedSalary, statutoryConfig) => {
      const findings = [];
      
      // Basic+DA ≥ 50% of Wages rule
      const basic = calculatedSalary.components.find(c => c.code === 'BASIC')?.calculatedValue || 0;
      const da = calculatedSalary.components.find(c => c.code === 'DA')?.calculatedValue || 0;
      const wages = calculatedSalary.summary.monthlyGross;
      const basicPlusDA = basic + da;
      
      if (basicPlusDA < wages * 0.5) {
        findings.push({
          ruleId: 'BASIC_DA_50_PERCENT',
          severity: 'ERROR',
          message: `Basic+DA (${basicPlusDA}) is less than 50% of wages (${wages * 0.5})`,
          suggestedFix: {
            type: 'AUTO_ADJUST_BASDA',
            previewDelta: wages * 0.5 - basicPlusDA
          }
        });
      }
      
      // PF compliance
      if (statutoryConfig) {
        const pfWage = basic + da;
        const pfCeiling = statutoryConfig.pf.wageCeiling;
        
        if (pfWage > pfCeiling && !statutoryConfig.pf.voluntaryAboveCeiling) {
          findings.push({
            ruleId: 'PF_CEILING_VIOLATION',
            severity: 'WARN',
            message: `PF wage (${pfWage}) exceeds ceiling (${pfCeiling})`,
            suggestedFix: {
              type: 'ENFORCE_PF_CEILING',
              previewDelta: pfWage - pfCeiling
            }
          });
        }
      }
      
      return findings;
    };
  
    // Save version function
    const saveVersion = (structureId, changeNote) => {
      const structure = structures.find(s => s.id === structureId);
      if (structure) {
        const newVersion = {
          id: Date.now(),
          structureId,
          version: `v${versions.filter(v => v.structureId === structureId).length + 1}`,
          changeNote,
          changedBy: null,
          changedAt: new Date().toISOString(),
          structureSnapshot: { ...structure }
        };
        saveVersions([...versions, newVersion]);
        return newVersion;
      }
    };
  
    // Create scenario function
    const createScenario = (structureId, name, overrides) => {
      const structure = structures.find(s => s.id === structureId);
      if (structure) {
        const newScenario = {
          id: Date.now(),
          structureId,
          name,
          overrides,
          createdAt: new Date().toISOString(),
          comparisonSummary: null
        };
        saveScenarios([...scenarios, newScenario]);
        return newScenario;
      }
    };
  
    // Formula Builder Component
    const FormulaBuilder = ({ isOpen, onClose, component, onSave }) => {
      const [formula, setFormula] = useState(component?.valueExpression || '');
      const [availableComponents, setAvailableComponents] = useState([]);
      const [verificationResult, setVerificationResult] = useState(null);
      const [testValue, setTestValue] = useState('100000'); // Default CTC for testing
  
      useEffect(() => {
        // Get all components including standard tokens
        const allComponents = [
          { name: 'BASIC', type: 'token', value: '50000' },
          { name: 'DA', type: 'token', value: '2500' },
          { name: 'HRA', type: 'token', value: '20000' },
          { name: 'GROSS_EARNINGS', type: 'token', value: '72500' },
          { name: 'CTC', type: 'token', value: testValue },
          { name: 'PF_EMP', type: 'token', value: '6000' },
          { name: 'PF_EMPR', type: 'token', value: '6000' },
          { name: 'ESI_EMP', type: 'token', value: '543' },
          { name: 'ESI_EMPR', type: 'token', value: '2356' },
          ...components.filter(comp => comp.id !== component?.id).map(comp => ({
            name: comp.code || comp.name,
            type: 'component',
            value: comp.valueExpression || '0'
          }))
        ];
        setAvailableComponents(allComponents);
      }, [component, components, testValue]);
  
      const addToFormula = (text) => {
        setFormula(prev => prev + text);
      };
  
      const verifyFormula = () => {
        try {
          if (!formula.trim()) {
            setVerificationResult({ valid: false, error: 'Formula cannot be empty' });
            return;
          }
  
          // Create a safe evaluation environment
          const safeEval = (expr) => {
            // Replace component names with test values
            let testFormula = formula;
            availableComponents.forEach(comp => {
              const regex = new RegExp(`\\b${comp.name}\\b`, 'g');
              testFormula = testFormula.replace(regex, comp.value || '0');
            });
  
            // Basic validation - only allow safe operations
            const allowedChars = /^[0-9+\-*/().\s]+$/;
            if (!allowedChars.test(testFormula)) {
              throw new Error('Formula contains invalid characters');
            }
  
            // Evaluate the formula
            const result = eval(testFormula);
            return isFinite(result) ? result : 0;
          };
  
          const result = safeEval(formula);
          setVerificationResult({ 
            valid: true, 
            result: result,
            message: `Formula is valid. Test result: ₹${result.toFixed(2)}`
          });
        } catch (error) {
          setVerificationResult({ 
            valid: false, 
            error: `Formula error: ${error.message}` 
          });
        }
      };
  
      const handleSave = () => {
        if (!verificationResult?.valid) {
          toast.error('Please verify the formula before saving');
          return;
        }
        onSave(formula);
        onClose();
      };
  
      if (!isOpen) return null;
  
      return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className={`p-6 rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto ${darkMode ? 'bg-[hsl(var(--ds-surface))] text-white' : 'bg-[hsl(var(--ds-surface))] text-[hsl(var(--ds-fg))]'}`}>
            <div className="flex justify-between items-center mb-4">
              <h3 className={`text-lg font-semibold ${darkMode ? 'text-white' : 'text-[hsl(var(--ds-fg))]'}`}>
                Advanced Formula Builder
              </h3>
              <button onClick={onClose} className="text-[hsl(var(--ds-muted-fg))] hover:text-[hsl(var(--ds-fg))]">
                <X size={20} />
              </button>
            </div>
  
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Left Column - Formula Building */}
              <div className="space-y-4">
                {/* Available Components */}
                <div>
                  <h4 className={`text-sm font-semibold mb-2 ${darkMode ? 'text-[hsl(var(--ds-muted-fg))]' : 'text-[hsl(var(--ds-fg))]'}`}>
                    Available Components & Variables
                  </h4>
                  <div className="flex flex-wrap gap-2">
                    {availableComponents.map(comp => (
                      <button
                        key={comp.name}
                        onClick={() => addToFormula(comp.name)}
                        className={`px-3 py-1 rounded text-xs font-medium ${
                          comp.type === 'token' 
                            ? (darkMode ? 'bg-[hsl(var(--ds-secondary))] text-white hover:bg-[hsl(var(--ds-secondary))]' : 'bg-[hsl(var(--ds-accent))] text-[hsl(var(--ds-secondary))] hover:bg-[hsl(var(--ds-accent))]')
                            : (darkMode ? 'bg-green-600 text-white hover:bg-green-700' : 'bg-green-100 text-green-700 hover:bg-green-200')
                        } transition-colors`}
                        title={`${comp.name}: ${comp.value || '0'}`}
                      >
                        {comp.name}
                      </button>
                    ))}
                  </div>
                </div>
  
                {/* Operators */}
                <div>
                  <h4 className={`text-sm font-semibold mb-2 ${darkMode ? 'text-[hsl(var(--ds-muted-fg))]' : 'text-[hsl(var(--ds-fg))]'}`}>
                    Operators
                  </h4>
                  <div className="flex flex-wrap gap-2">
                    {['+', '-', '*', '/', '(', ')', '%'].map(op => (
                      <button
                        key={op}
                        onClick={() => addToFormula(op)}
                        className={`px-3 py-1 rounded text-xs font-medium ${
                          darkMode ? 'bg-[hsl(var(--ds-muted))] text-white hover:bg-[hsl(var(--ds-muted))]' : 'bg-[hsl(var(--ds-muted))] text-[hsl(var(--ds-fg))] hover:bg-[hsl(var(--ds-muted))]'
                        } transition-colors`}
                      >
                        {op}
                      </button>
                    ))}
                  </div>
                </div>
  
                {/* Functions */}
                <div>
                  <h4 className={`text-sm font-semibold mb-2 ${darkMode ? 'text-[hsl(var(--ds-muted-fg))]' : 'text-[hsl(var(--ds-fg))]'}`}>
                    Functions
                  </h4>
                  <div className="flex flex-wrap gap-2">
                    {['Math.round()', 'Math.floor()', 'Math.ceil()', 'Math.min()', 'Math.max()'].map(func => (
                      <button
                        key={func}
                        onClick={() => addToFormula(func)}
                        className={`px-3 py-1 rounded text-xs font-medium ${
                          darkMode ? 'bg-[hsl(var(--ds-muted))] text-[hsl(var(--ds-fg))] hover:bg-[hsl(var(--ds-muted))]' : 'bg-[hsl(var(--ds-muted))] text-[hsl(var(--ds-fg))] hover:bg-[hsl(var(--ds-muted))]'
                        } transition-colors`}
                      >
                        {func}
                      </button>
                    ))}
                  </div>
                </div>
  
                {/* Formula Input */}
                <div>
                  <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-[hsl(var(--ds-muted-fg))]' : 'text-[hsl(var(--ds-fg))]'}`}>
                    Formula
                  </label>
                  <textarea
                    value={formula}
                    onChange={(e) => setFormula(e.target.value)}
                    className={`w-full p-3 border rounded text-sm ${darkMode ? 'bg-[hsl(var(--ds-muted))] border-[hsl(var(--ds-border))] text-white' : 'bg-[hsl(var(--ds-surface))] border-[hsl(var(--ds-border))]'}`}
                    rows={6}
                    placeholder="Enter formula (e.g., CTC * 0.4 + Basic * 0.1)"
                  />
                </div>
  
                {/* Test Value */}
                <div>
                  <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-[hsl(var(--ds-muted-fg))]' : 'text-[hsl(var(--ds-fg))]'}`}>
                    Test CTC Value
                  </label>
                  <input
                    type="number"
                    value={testValue}
                    onChange={(e) => setTestValue(e.target.value)}
                    className={`w-full p-2 border rounded text-sm ${darkMode ? 'bg-[hsl(var(--ds-muted))] border-[hsl(var(--ds-border))] text-white' : 'bg-[hsl(var(--ds-surface))] border-[hsl(var(--ds-border))]'}`}
                    placeholder="Enter test CTC value"
                  />
                </div>
              </div>
  
              {/* Right Column - Verification & Preview */}
              <div className="space-y-4">
                {/* Verification */}
                <div>
                  <h4 className={`text-sm font-semibold mb-2 ${darkMode ? 'text-[hsl(var(--ds-muted-fg))]' : 'text-[hsl(var(--ds-fg))]'}`}>
                    Formula Verification
                  </h4>
                  <DsButton type="button" variant="primary" onClick={verifyFormula} className="w-full">
                      Verify Formula
                    </DsButton>
                  
                  {verificationResult && (
                    <div className={`mt-3 p-3 rounded ${
                      verificationResult.valid 
                        ? (darkMode ? 'bg-green-900 text-green-200' : 'bg-green-100 text-green-800')
                        : (darkMode ? 'bg-red-900 text-red-200' : 'bg-red-100 text-red-800')
                    }`}>
                      <p className="text-sm font-medium">
                        {verificationResult.valid ? '✓ Valid' : '✗ Invalid'}
                      </p>
                      <p className="text-xs mt-1">
                        {verificationResult.valid ? verificationResult.message : verificationResult.error}
                      </p>
                    </div>
                  )}
                </div>
  
                {/* Preview */}
                <div>
                  <h4 className={`text-sm font-semibold mb-2 ${darkMode ? 'text-[hsl(var(--ds-muted-fg))]' : 'text-[hsl(var(--ds-fg))]'}`}>
                    Formula Preview
                  </h4>
                  <div className={`p-3 rounded ${darkMode ? 'bg-[hsl(var(--ds-muted))]' : 'bg-[hsl(var(--ds-muted))]'}`}>
                    <p className={`text-sm font-mono ${darkMode ? 'text-[hsl(var(--ds-muted-fg))]' : 'text-[hsl(var(--ds-muted-fg))]'}`}>
                      {formula || 'No formula entered'}
                    </p>
                  </div>
                </div>
  
                {/* Component Dependencies */}
                <div>
                  <h4 className={`text-sm font-semibold mb-2 ${darkMode ? 'text-[hsl(var(--ds-muted-fg))]' : 'text-[hsl(var(--ds-fg))]'}`}>
                    Component Dependencies
                  </h4>
                  <div className={`p-3 rounded ${darkMode ? 'bg-[hsl(var(--ds-muted))]' : 'bg-[hsl(var(--ds-muted))]'}`}>
                    {availableComponents.filter(comp => formula.includes(comp.name)).length > 0 ? (
                      <div className="space-y-1">
                        {availableComponents
                          .filter(comp => formula.includes(comp.name))
                          .map(comp => (
                            <div key={comp.name} className="flex justify-between text-xs">
                              <span className={`${darkMode ? 'text-[hsl(var(--ds-muted-fg))]' : 'text-[hsl(var(--ds-muted-fg))]'}`}>
                                {comp.name}
                              </span>
                              <span className={`font-mono ${darkMode ? 'text-[hsl(var(--ds-muted-fg))]' : 'text-[hsl(var(--ds-fg))]'}`}>
                                ₹{comp.value || '0'}
                              </span>
                            </div>
                          ))}
                      </div>
                    ) : (
                      <p className={`text-xs ${darkMode ? 'text-[hsl(var(--ds-muted-fg))]' : 'text-[hsl(var(--ds-muted-fg))]'}`}>
                        No dependencies found
                      </p>
                    )}
                  </div>
                </div>
              </div>
            </div>
  
            {/* Action Buttons */}
            <div className="flex justify-end space-x-3 mt-6">
              <DsButton type="button" size="sm" variant="secondary" onClick={onClose}>
                  Cancel
                </DsButton>
              <DsButton type="button" size="sm" variant="primary" onClick={handleSave} disabled={!verificationResult?.valid}>
                  Save Formula
                </DsButton>
            </div>
          </div>
        </div>
      );
    };
  
    // Template Tab
    const renderTemplateTab = () => (
      <div className={`p-4 rounded-lg ${darkMode ? 'bg-[hsl(var(--ds-surface))]' : 'bg-[hsl(var(--ds-surface))]'} shadow-md`}>
        <div className="flex justify-between items-center mb-4">
          <h2 className={`text-lg font-bold ${darkMode ? 'text-white' : 'text-[hsl(var(--ds-fg))]'}`}>
            Salary Structure Templates
          </h2>
          <DsButton type="button" size="sm" variant="primary" onClick={addTemplate}>
              Create Template
            </DsButton>
        </div>
  
        {/* Preset Templates */}
        <div className="mb-6">
          <h3 className={`text-md font-semibold mb-3 ${darkMode ? 'text-white' : 'text-[hsl(var(--ds-fg))]'}`}>
            Preset Templates
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <button
              onClick={() => {
                const entryTemplate = {
                  id: Date.now(),
                  name: 'Entry Level Template',
                  mode: 'CTC',
                  components: [
                    { code: 'BASIC', calculationType: 'PERCENT_OF_CTC', valueExpression: '45' },
                    { code: 'HRA', calculationType: 'PERCENT_OF_BASIC', valueExpression: '40' },
                    { code: 'DA', calculationType: 'PERCENT_OF_BASIC', valueExpression: '5' },
                    { code: 'SPECIAL_ALLOWANCE', calculationType: 'BALANCING', valueExpression: '' }
                  ],
                  defaultCityType: 'NON_METRO',
                  active: true
                };
                saveTemplates([...templates, entryTemplate]);
              }}
              className={`p-4 rounded-lg border text-left ${
                darkMode ? 'border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-muted))] hover:bg-[hsl(var(--ds-muted))]' : 'border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-muted))] hover:bg-[hsl(var(--ds-muted))]'
              } transition-colors`}
            >
              <h4 className={`font-semibold ${darkMode ? 'text-white' : 'text-[hsl(var(--ds-fg))]'}`}>Entry Level</h4>
              <p className={`text-sm ${darkMode ? 'text-[hsl(var(--ds-muted-fg))]' : 'text-[hsl(var(--ds-muted-fg))]'}`}>
                Basic 45% of CTC, HRA 40% of Basic
              </p>
            </button>
  
            <button
              onClick={() => {
                const midTemplate = {
                  id: Date.now(),
                  name: 'Mid Level Template',
                  mode: 'CTC',
                  components: [
                    { code: 'BASIC', calculationType: 'PERCENT_OF_CTC', valueExpression: '50' },
                    { code: 'HRA', calculationType: 'PERCENT_OF_BASIC', valueExpression: '40' },
                    { code: 'DA', calculationType: 'PERCENT_OF_BASIC', valueExpression: '5' },
                    { code: 'SPECIAL_ALLOWANCE', calculationType: 'BALANCING', valueExpression: '' }
                  ],
                  defaultCityType: 'NON_METRO',
                  active: true
                };
                saveTemplates([...templates, midTemplate]);
              }}
              className={`p-4 rounded-lg border text-left ${
                darkMode ? 'border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-muted))] hover:bg-[hsl(var(--ds-muted))]' : 'border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-muted))] hover:bg-[hsl(var(--ds-muted))]'
              } transition-colors`}
            >
              <h4 className={`font-semibold ${darkMode ? 'text-white' : 'text-[hsl(var(--ds-fg))]'}`}>Mid Level</h4>
              <p className={`text-sm ${darkMode ? 'text-[hsl(var(--ds-muted-fg))]' : 'text-[hsl(var(--ds-muted-fg))]'}`}>
                Basic 50% of CTC, HRA 40% of Basic
              </p>
            </button>
  
            <button
              onClick={() => {
                const seniorTemplate = {
                  id: Date.now(),
                  name: 'Senior Level Template',
                  mode: 'CTC',
                  components: [
                    { code: 'BASIC', calculationType: 'PERCENT_OF_CTC', valueExpression: '50' },
                    { code: 'HRA', calculationType: 'PERCENT_OF_BASIC', valueExpression: '50' },
                    { code: 'DA', calculationType: 'PERCENT_OF_BASIC', valueExpression: '10' },
                    { code: 'SPECIAL_ALLOWANCE', calculationType: 'BALANCING', valueExpression: '' }
                  ],
                  defaultCityType: 'METRO',
                  active: true
                };
                saveTemplates([...templates, seniorTemplate]);
              }}
              className={`p-4 rounded-lg border text-left ${
                darkMode ? 'border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-muted))] hover:bg-[hsl(var(--ds-muted))]' : 'border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-muted))] hover:bg-[hsl(var(--ds-muted))]'
              } transition-colors`}
            >
              <h4 className={`font-semibold ${darkMode ? 'text-white' : 'text-[hsl(var(--ds-fg))]'}`}>Senior Level</h4>
              <p className={`text-sm ${darkMode ? 'text-[hsl(var(--ds-muted-fg))]' : 'text-[hsl(var(--ds-muted-fg))]'}`}>
                Basic 50% of CTC, HRA 50% of Basic (Metro)
              </p>
            </button>
          </div>
        </div>
  
        {/* Custom Templates */}
        <div>
          <h3 className={`text-md font-semibold mb-3 ${darkMode ? 'text-white' : 'text-[hsl(var(--ds-fg))]'}`}>
            Custom Templates
          </h3>
          <div className="space-y-4">
            {templates.map(template => (
              <div key={template.id} className={`p-4 rounded-lg border ${darkMode ? 'border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-muted))]' : 'border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-muted))]'}`}>
                <div className="flex justify-between items-start">
                  <div>
                    <h4 className={`text-md font-semibold ${darkMode ? 'text-white' : 'text-[hsl(var(--ds-fg))]'}`}>
                      {template.name}
                    </h4>
                    <p className={`text-sm ${darkMode ? 'text-[hsl(var(--ds-muted-fg))]' : 'text-[hsl(var(--ds-muted-fg))]'}`}>
                      Mode: {template.mode} | City: {template.defaultCityType}
                    </p>
                  </div>
                  <div className="flex space-x-2">
                    <button
                      onClick={() => setSelectedTemplate(template)}
                      className={`px-3 py-1 rounded text-xs font-medium ${
                        darkMode ? 'bg-[hsl(var(--ds-secondary))] text-white hover:bg-[hsl(var(--ds-secondary))]' : 'bg-[hsl(var(--ds-primary))] text-white hover:bg-[hsl(var(--ds-primary-hover))]'
                      } transition-colors`}
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => deleteTemplate(template.id)}
                      className={`px-3 py-1 rounded text-xs font-medium ${
                        darkMode ? 'bg-[hsl(var(--ds-primary))] text-[hsl(var(--ds-primary-fg))] hover:bg-[hsl(var(--ds-primary-hover))]' : 'bg-[hsl(var(--ds-primary))] text-[hsl(var(--ds-primary-fg))] hover:bg-[hsl(var(--ds-primary-hover))]'
                      } transition-colors`}
                    >
                      Delete
                    </button>
                  </div>
                </div>
              </div>
            ))}
  
            {templates.length === 0 && (
              <div className={`text-center py-8 ${darkMode ? 'text-[hsl(var(--ds-muted-fg))]' : 'text-[hsl(var(--ds-muted-fg))]'}`}>
                No custom templates created yet. Click "Create Template" or use preset templates to get started.
              </div>
            )}
          </div>
        </div>
      </div>
    );
  
    // Statutory Tab
    const renderStatutoryTab = () => (
      <div className={`p-4 rounded-lg ${darkMode ? 'bg-[hsl(var(--ds-surface))]' : 'bg-[hsl(var(--ds-surface))]'} shadow-md`}>
        <div className="flex justify-between items-center mb-4">
          <h2 className={`text-lg font-bold ${darkMode ? 'text-white' : 'text-[hsl(var(--ds-fg))]'}`}>
            Statutory Configuration
          </h2>
          <DsButton
            type="button"
            size="sm"
            variant="primary"
            onClick={() => setShowStatutoryModal(true)}
          >
            Add Statutory Config
          </DsButton>
        </div>
  
        <div className="space-y-4">
          {statutoryConfigs.map(config => (
            <div key={config.id} className={`p-4 rounded-lg border ${darkMode ? 'border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-muted))]' : 'border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-muted))]'}`}>
              <div className="flex justify-between items-start">
                <div>
                  <h3 className={`text-md font-semibold ${darkMode ? 'text-white' : 'text-[hsl(var(--ds-fg))]'}`}>
                    {config.name}
                  </h3>
                  <div className={`text-sm ${darkMode ? 'text-[hsl(var(--ds-muted-fg))]' : 'text-[hsl(var(--ds-muted-fg))]'} mt-2`}>
                    <div>PF: Employee {config.pf.employeePct}% | Employer {config.pf.employerPct}% | Ceiling ₹{config.pf.wageCeiling?.toLocaleString()}</div>
                    <div>ESI: Employee {config.esi.employeePct}% | Employer {config.esi.employerPct}% | Eligibility ₹{config.esi.eligibilityGrossMonthly?.toLocaleString()}</div>
                    <div>Gratuity: {config.gratuity.ratePct}% of {config.gratuity.basis}</div>
                    {config.effectiveFrom && (
                      <div>Effective From: {formatDate(config.effectiveFrom)}</div>
                    )}
                  </div>
                </div>
                <div className="flex space-x-2">
                  <button
                    onClick={() => {
                      setSelectedStatutoryConfig(config);
                      setShowStatutoryModal(true);
                    }}
                    className={`px-3 py-1 rounded text-xs font-medium ${
                      darkMode ? 'bg-[hsl(var(--ds-secondary))] text-white hover:bg-[hsl(var(--ds-secondary))]' : 'bg-[hsl(var(--ds-primary))] text-white hover:bg-[hsl(var(--ds-primary-hover))]'
                    } transition-colors`}
                  >
                    Edit
                  </button>
                  <button
                    onClick={() => deleteStatutoryConfig(config.id)}
                    className={`px-3 py-1 rounded text-xs font-medium ${
                      darkMode ? 'bg-[hsl(var(--ds-primary))] text-[hsl(var(--ds-primary-fg))] hover:bg-[hsl(var(--ds-primary-hover))]' : 'bg-[hsl(var(--ds-primary))] text-[hsl(var(--ds-primary-fg))] hover:bg-[hsl(var(--ds-primary-hover))]'
                    } transition-colors`}
                  >
                    Delete
                  </button>
                </div>
              </div>
            </div>
          ))}
  
          {statutoryConfigs.length === 0 && (
            <div className={`text-center py-8 ${darkMode ? 'text-[hsl(var(--ds-muted-fg))]' : 'text-[hsl(var(--ds-muted-fg))]'}`}>
              No statutory configurations created yet. Click "Add Statutory Config" to get started.
            </div>
          )}
        </div>
  
        {/* Statutory Configuration Modal */}
        {showStatutoryModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999]">
            <div className={`p-6 rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto ${darkMode ? 'bg-[hsl(var(--ds-surface))] text-white' : 'bg-[hsl(var(--ds-surface))] text-[hsl(var(--ds-fg))]'}`}>
              <div className="flex justify-between items-center mb-4">
                <h3 className={`text-lg font-semibold ${darkMode ? 'text-white' : 'text-[hsl(var(--ds-fg))]'}`}>
                  {selectedStatutoryConfig ? 'Edit' : 'Create'} Statutory Configuration
                </h3>
                <button
                  onClick={() => {
                    setShowStatutoryModal(false);
                    setSelectedStatutoryConfig(null);
                  }}
                  className="text-[hsl(var(--ds-muted-fg))] hover:text-[hsl(var(--ds-fg))]"
                >
                  <X size={20} />
                </button>
              </div>
  
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Basic Info */}
                <div className="space-y-4">
                  <h4 className={`text-md font-semibold ${darkMode ? 'text-white' : 'text-[hsl(var(--ds-fg))]'}`}>
                    Basic Information
                  </h4>
                  
                  <div>
                    <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-[hsl(var(--ds-muted-fg))]' : 'text-[hsl(var(--ds-fg))]'}`}>
                      Configuration Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      id="statutoryName"
                      defaultValue={selectedStatutoryConfig?.name || ''}
                      className={`w-full p-2 border rounded text-sm ${darkMode ? 'bg-[hsl(var(--ds-muted))] border-[hsl(var(--ds-border))] text-white' : 'bg-[hsl(var(--ds-surface))] border-[hsl(var(--ds-border))]'}`}
                      placeholder="e.g., Default Statutory Config"
                    />
                  </div>
  
                  <div>
                    <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-[hsl(var(--ds-muted-fg))]' : 'text-[hsl(var(--ds-fg))]'}`}>
                      Effective From <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="date"
                      id="statutoryEffectiveFrom"
                      defaultValue={selectedStatutoryConfig?.effectiveFrom || ''}
                      min={companyMinDate}
                      className={`w-full p-2 border rounded text-sm ${darkMode ? 'bg-[hsl(var(--ds-muted))] border-[hsl(var(--ds-border))] text-white' : 'bg-[hsl(var(--ds-surface))] border-[hsl(var(--ds-border))]'}`}
                    />
                  </div>
                </div>
  
                {/* PF Configuration */}
                <div className="space-y-4">
                  <h4 className={`text-md font-semibold ${darkMode ? 'text-white' : 'text-[hsl(var(--ds-fg))]'}`}>
                    Provident Fund (PF)
                  </h4>
                  
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-[hsl(var(--ds-muted-fg))]' : 'text-[hsl(var(--ds-fg))]'}`}>
                        Employee Share (%) <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="number"
                        step="0.01"
                        id="pfEmployeePct"
                        defaultValue={selectedStatutoryConfig?.pf?.employeePct || 12}
                        className={`w-full p-2 border rounded text-sm ${darkMode ? 'bg-[hsl(var(--ds-muted))] border-[hsl(var(--ds-border))] text-white' : 'bg-[hsl(var(--ds-surface))] border-[hsl(var(--ds-border))]'}`}
                      />
                    </div>
                    <div>
                      <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-[hsl(var(--ds-muted-fg))]' : 'text-[hsl(var(--ds-fg))]'}`}>
                        Employer Share (%) <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="number"
                        step="0.01"
                        id="pfEmployerPct"
                        defaultValue={selectedStatutoryConfig?.pf?.employerPct || 12}
                        className={`w-full p-2 border rounded text-sm ${darkMode ? 'bg-[hsl(var(--ds-muted))] border-[hsl(var(--ds-border))] text-white' : 'bg-[hsl(var(--ds-surface))] border-[hsl(var(--ds-border))]'}`}
                      />
                    </div>
                  </div>
  
                  <div>
                    <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-[hsl(var(--ds-muted-fg))]' : 'text-[hsl(var(--ds-fg))]'}`}>
                      Wage Ceiling (₹) <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="number"
                      id="pfWageCeiling"
                      defaultValue={selectedStatutoryConfig?.pf?.wageCeiling || 15000}
                      className={`w-full p-2 border rounded text-sm ${darkMode ? 'bg-[hsl(var(--ds-muted))] border-[hsl(var(--ds-border))] text-white' : 'bg-[hsl(var(--ds-surface))] border-[hsl(var(--ds-border))]'}`}
                    />
                  </div>
  
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="pfApplyCeiling"
                      defaultChecked={selectedStatutoryConfig?.pf?.applyCeiling !== false}
                      className="mr-2"
                    />
                    <span className={`text-sm ${darkMode ? 'text-[hsl(var(--ds-muted-fg))]' : 'text-[hsl(var(--ds-fg))]'}`}>
                      Apply ceiling limit
                    </span>
                  </div>
                </div>
  
                {/* ESI Configuration */}
                <div className="space-y-4">
                  <h4 className={`text-md font-semibold ${darkMode ? 'text-white' : 'text-[hsl(var(--ds-fg))]'}`}>
                    Employee State Insurance (ESI)
                  </h4>
                  
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-[hsl(var(--ds-muted-fg))]' : 'text-[hsl(var(--ds-fg))]'}`}>
                        Employee Share (%) <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="number"
                        step="0.01"
                        id="esiEmployeePct"
                        defaultValue={selectedStatutoryConfig?.esi?.employeePct || 0.75}
                        className={`w-full p-2 border rounded text-sm ${darkMode ? 'bg-[hsl(var(--ds-muted))] border-[hsl(var(--ds-border))] text-white' : 'bg-[hsl(var(--ds-surface))] border-[hsl(var(--ds-border))]'}`}
                      />
                    </div>
                    <div>
                      <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-[hsl(var(--ds-muted-fg))]' : 'text-[hsl(var(--ds-fg))]'}`}>
                        Employer Share (%) <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="number"
                        step="0.01"
                        id="esiEmployerPct"
                        defaultValue={selectedStatutoryConfig?.esi?.employerPct || 3.25}
                        className={`w-full p-2 border rounded text-sm ${darkMode ? 'bg-[hsl(var(--ds-muted))] border-[hsl(var(--ds-border))] text-white' : 'bg-[hsl(var(--ds-surface))] border-[hsl(var(--ds-border))]'}`}
                      />
                    </div>
                  </div>
  
                  <div>
                    <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-[hsl(var(--ds-muted-fg))]' : 'text-[hsl(var(--ds-fg))]'}`}>
                      Eligibility Gross Monthly (₹) <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="number"
                      id="esiEligibilityGross"
                      defaultValue={selectedStatutoryConfig?.esi?.eligibilityGrossMonthly || 21000}
                      className={`w-full p-2 border rounded text-sm ${darkMode ? 'bg-[hsl(var(--ds-muted))] border-[hsl(var(--ds-border))] text-white' : 'bg-[hsl(var(--ds-surface))] border-[hsl(var(--ds-border))]'}`}
                    />
                  </div>
                </div>
  
                {/* Gratuity Configuration */}
                <div className="space-y-4">
                  <h4 className={`text-md font-semibold ${darkMode ? 'text-white' : 'text-[hsl(var(--ds-fg))]'}`}>
                    Gratuity
                  </h4>
                  
                  <div>
                    <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-[hsl(var(--ds-muted-fg))]' : 'text-[hsl(var(--ds-fg))]'}`}>
                      Calculation Basis <span className="text-red-500">*</span>
                    </label>
                    <select
                      id="gratuityBasis"
                      defaultValue={selectedStatutoryConfig?.gratuity?.basis || 'BASIC'}
                      className={`w-full p-2 border rounded text-sm ${darkMode ? 'bg-[hsl(var(--ds-muted))] border-[hsl(var(--ds-border))] text-white' : 'bg-[hsl(var(--ds-surface))] border-[hsl(var(--ds-border))]'}`}
                    >
                      <option value="BASIC">Basic Salary</option>
                      <option value="BASIC_DA">Basic + DA</option>
                      <option value="GROSS">Gross Salary</option>
                    </select>
                  </div>
  
                  <div>
                    <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-[hsl(var(--ds-muted-fg))]' : 'text-[hsl(var(--ds-fg))]'}`}>
                      Rate (%) <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      id="gratuityRate"
                      defaultValue={selectedStatutoryConfig?.gratuity?.ratePct || 4.81}
                      className={`w-full p-2 border rounded text-sm ${darkMode ? 'bg-[hsl(var(--ds-muted))] border-[hsl(var(--ds-border))] text-white' : 'bg-[hsl(var(--ds-surface))] border-[hsl(var(--ds-border))]'}`}
                    />
                  </div>
                </div>
              </div>
  
              {/* Action Buttons */}
              <div className="flex justify-end space-x-3 mt-6">
                <button
                  onClick={() => {
                    setShowStatutoryModal(false);
                    setSelectedStatutoryConfig(null);
                  }}
                  className={`px-4 py-2 rounded text-sm font-medium ${
                    darkMode ? 'bg-[hsl(var(--ds-muted))] text-white hover:bg-[hsl(var(--ds-muted))]' : 'bg-[hsl(var(--ds-muted))] text-[hsl(var(--ds-fg))] hover:bg-[hsl(var(--ds-muted))]'
                  } transition-colors`}
                >
                  Cancel
                </button>
                <button
                  onClick={() => {
                    const name = document.getElementById('statutoryName').value.trim();
                    const effectiveFrom = document.getElementById('statutoryEffectiveFrom').value;
                    const pfEmployeePct = parseFloat(document.getElementById('pfEmployeePct').value);
                    const pfEmployerPct = parseFloat(document.getElementById('pfEmployerPct').value);
                    const pfWageCeiling = parseFloat(document.getElementById('pfWageCeiling').value);
                    const pfApplyCeiling = document.getElementById('pfApplyCeiling').checked;
                    const esiEmployeePct = parseFloat(document.getElementById('esiEmployeePct').value);
                    const esiEmployerPct = parseFloat(document.getElementById('esiEmployerPct').value);
                    const esiEligibilityGross = parseFloat(document.getElementById('esiEligibilityGross').value);
                    const gratuityBasis = document.getElementById('gratuityBasis').value;
                    const gratuityRate = parseFloat(document.getElementById('gratuityRate').value);
  
                    if (!name || !effectiveFrom) {
                      toast.error('Please fill in all required fields');
                      return;
                    }
  
                    const newConfig = {
                      id: selectedStatutoryConfig?.id || Date.now(),
                      name,
                      effectiveFrom,
                      pf: {
                        employeePct: pfEmployeePct,
                        employerPct: pfEmployerPct,
                        wageCeiling: pfWageCeiling,
                        applyCeiling: pfApplyCeiling
                      },
                      esi: {
                        employeePct: esiEmployeePct,
                        employerPct: esiEmployerPct,
                        eligibilityGrossMonthly: esiEligibilityGross
                      },
                      gratuity: {
                        basis: gratuityBasis,
                        ratePct: gratuityRate
                      }
                    };
  
                    if (selectedStatutoryConfig) {
                      // Update existing config
                      const updatedConfigs = statutoryConfigs.map(config => 
                        config.id === selectedStatutoryConfig.id ? newConfig : config
                      );
                      saveStatutoryConfigs(updatedConfigs);
                      toast.success('Statutory configuration updated successfully!');
                    } else {
                      // Create new config
                      saveStatutoryConfigs([...statutoryConfigs, newConfig]);
                      toast.success('Statutory configuration created successfully!');
                    }
  
                    setShowStatutoryModal(false);
                    setSelectedStatutoryConfig(null);
                  }}
                  className={`px-4 py-2 rounded text-sm font-medium ${
                    darkMode ? 'bg-[hsl(var(--ds-secondary))] text-white hover:bg-[hsl(var(--ds-secondary))]' : 'bg-[hsl(var(--ds-primary))] text-white hover:bg-[hsl(var(--ds-primary-hover))]'
                  } transition-colors`}
                >
                  {selectedStatutoryConfig ? 'Update' : 'Create'} Configuration
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    );

    return (
      <div className="min-h-0 flex-1 overflow-hidden bg-[hsl(var(--ds-surface))] text-[hsl(var(--ds-fg))]">
        {/* Tab Navigation */}
        {!structuresOnly && (
        <div className="mb-3 flex flex-wrap border-b border-[hsl(var(--ds-border))]">
          {[
            { id: 'structures', label: 'Salary Structures' },
            { id: 'statutory', label: 'Statutory Rates' },
            ...(canViewTdsHeads
              ? [{ id: 'tds_heads', label: 'TDS Reference Heads' }]
              : []),
          ].map(tab => (
            <button
              key={tab.id}
              type="button"
              className={`px-3 py-2 text-sm font-medium transition-colors whitespace-nowrap ${
                activeTab === tab.id
                  ? 'border-b-2 border-[hsl(var(--ds-primary))] text-[hsl(var(--ds-secondary))]'
                  : 'text-[hsl(var(--ds-muted-fg))] hover:text-[hsl(var(--ds-fg))]'
              }`}
              onClick={() => setActiveTab(tab.id)}
            >
              {tab.label}
            </button>
          ))}
        </div>
        )}

        {/* Tab Content */}
        {(structuresOnly || activeTab === 'structures') && (
          <SalaryStructureManager 
            components={components}
            onSaveComponents={saveComponents}
            onCreateComponent={createComponentFromPanel}
            onUpdateComponent={updateComponent}
            onDeleteComponent={deleteComponent}
            onSyncLibraryFromStructures={syncLibraryFromStructures}
            darkMode={darkMode}
            onComponentUpdate={async () => {
              try {
                await refetchComponents();
              } catch {
                /* ignore */
              }
            }}
            organizationalLevels={{
              branches: branchesList,
              departments: departmentsList,
              designations: designationsList,
              grades: gradesList,
              categories: categoriesList,
              subDepartments: subDepartmentsList,
            }}
            statutoryRates={statutoryRates}
            statutoryConfigJson={statutoryConfigJson}
          />
        )}
        {!structuresOnly && activeTab === 'statutory' && <StatutoryRatesManager key="statutory-rates-pt-v2" />}
        {!structuresOnly && activeTab === 'tds_heads' && <TdsReferenceHeadsManager darkMode={darkMode} />}

        {/* Formula Builder Modal */}
        <FormulaBuilder
          isOpen={showFormulaBuilder && selectedComponent?.calculationType === 'FORMULA'}
          onClose={() => setShowFormulaBuilder(false)}
          component={selectedComponent}
          onSave={(valueExpression) => {
            if (selectedComponent) {
              updateComponent(selectedComponent.id, { valueExpression });
            }
            setShowFormulaBuilder(false);
            setSelectedComponent(null);
          }}
        />
  
        {/* Formula Manager Modal */}
        {showFormulaManager && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999]">
            <div className={`p-6 rounded-lg shadow-xl max-w-6xl w-full max-h-[90vh] overflow-y-auto ${darkMode ? 'bg-[hsl(var(--ds-surface))] text-white' : 'bg-[hsl(var(--ds-surface))] text-[hsl(var(--ds-fg))]'}`}>
              <div className="flex justify-between items-center mb-4">
                <h3 className={`text-lg font-semibold ${darkMode ? 'text-white' : 'text-[hsl(var(--ds-fg))]'}`}>
                  Advanced Formula Manager
                </h3>
                <button
                  onClick={() => setShowFormulaManager(false)}
                  className="text-[hsl(var(--ds-muted-fg))] hover:text-[hsl(var(--ds-fg))]"
                >
                  <X size={20} />
                </button>
              </div>
  
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Left Side - Formula Builder */}
                <div className={`p-4 rounded-lg border ${darkMode ? 'border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-muted))]' : 'border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-muted))]'}`}>
                  <h4 className={`text-base font-semibold mb-3 ${darkMode ? 'text-white' : 'text-[hsl(var(--ds-fg))]'}`}>
                    Formula Builder
                  </h4>
                  
                  {/* Available Components */}
                  <div className="mb-4">
                    <h5 className={`text-sm font-medium mb-2 ${darkMode ? 'text-[hsl(var(--ds-muted-fg))]' : 'text-[hsl(var(--ds-fg))]'}`}>
                      Available Components & Variables
                    </h5>
                    <div className="flex flex-wrap gap-2">
                      {/* Default Components */}
                      <button
                        onClick={() => addToFormula('BASIC')}
                        className={`px-3 py-1 rounded text-xs font-medium ${
                          darkMode ? 'bg-[hsl(var(--ds-secondary))] text-white hover:bg-[hsl(var(--ds-secondary))]' : 'bg-[hsl(var(--ds-accent))] text-[hsl(var(--ds-secondary))] hover:bg-[hsl(var(--ds-accent))]'
                        } transition-colors`}
                      >
                        BASIC
                      </button>
                      <button
                        onClick={() => addToFormula('DA')}
                        className={`px-3 py-1 rounded text-xs font-medium ${
                          darkMode ? 'bg-[hsl(var(--ds-secondary))] text-white hover:bg-[hsl(var(--ds-secondary))]' : 'bg-[hsl(var(--ds-accent))] text-[hsl(var(--ds-secondary))] hover:bg-[hsl(var(--ds-accent))]'
                        } transition-colors`}
                      >
                        DA
                      </button>
                      <button
                        onClick={() => addToFormula('HRA')}
                        className={`px-3 py-1 rounded text-xs font-medium ${
                          darkMode ? 'bg-[hsl(var(--ds-secondary))] text-white hover:bg-[hsl(var(--ds-secondary))]' : 'bg-[hsl(var(--ds-accent))] text-[hsl(var(--ds-secondary))] hover:bg-[hsl(var(--ds-accent))]'
                        } transition-colors`}
                      >
                        HRA
                      </button>
                      <button
                        onClick={() => addToFormula('GROSS_EARNINGS')}
                        className={`px-3 py-1 rounded text-xs font-medium ${
                          darkMode ? 'bg-[hsl(var(--ds-secondary))] text-white hover:bg-[hsl(var(--ds-secondary))]' : 'bg-[hsl(var(--ds-accent))] text-[hsl(var(--ds-secondary))] hover:bg-[hsl(var(--ds-accent))]'
                        } transition-colors`}
                      >
                        GROSS_EARNINGS
                      </button>
                      <button
                        onClick={() => addToFormula('CTC')}
                        className={`px-3 py-1 rounded text-xs font-medium ${
                          darkMode ? 'bg-[hsl(var(--ds-secondary))] text-white hover:bg-[hsl(var(--ds-secondary))]' : 'bg-[hsl(var(--ds-accent))] text-[hsl(var(--ds-secondary))] hover:bg-[hsl(var(--ds-accent))]'
                        } transition-colors`}
                      >
                        CTC
                      </button>
                      
                      {/* Dynamic Components */}
                      {components.map(comp => (
                        <button
                          key={comp.id}
                          onClick={() => addToFormula(comp.code || comp.name)}
                          className={`px-3 py-1 rounded text-xs font-medium ${
                            darkMode ? 'bg-green-600 text-white hover:bg-green-700' : 'bg-green-100 text-green-800 hover:bg-green-200'
                          } transition-colors`}
                        >
                          {comp.code || comp.name}
                        </button>
                      ))}
                    </div>
                  </div>
  
                  {/* Operators */}
                  <div className="mb-4">
                    <h5 className={`text-sm font-medium mb-2 ${darkMode ? 'text-[hsl(var(--ds-muted-fg))]' : 'text-[hsl(var(--ds-fg))]'}`}>
                      Operators
                    </h5>
                    <div className="flex flex-wrap gap-2">
                      {['+', '-', '*', '/', '(', ')', 'Math.round', 'Math.min', 'Math.max'].map(op => (
                        <button
                          key={op}
                          onClick={() => addToFormula(op)}
                          className={`px-3 py-1 rounded text-xs font-medium ${
                            darkMode ? 'bg-[hsl(var(--ds-muted))] text-[hsl(var(--ds-fg))] hover:bg-[hsl(var(--ds-muted))]' : 'bg-[hsl(var(--ds-muted))] text-[hsl(var(--ds-fg))] hover:bg-[hsl(var(--ds-muted))]'
                          } transition-colors`}
                        >
                          {op}
                        </button>
                      ))}
                    </div>
                  </div>
  
                  {/* Formula Input */}
                  <div className="mb-4">
                    <h5 className={`text-sm font-medium mb-2 ${darkMode ? 'text-[hsl(var(--ds-muted-fg))]' : 'text-[hsl(var(--ds-fg))]'}`}>
                      Formula
                    </h5>
                    <textarea
                      value={selectedFormula?.formula || ''}
                      onChange={(e) => setSelectedFormula(prev => ({ ...prev, formula: e.target.value }))}
                      placeholder="Enter your formula here..."
                      rows={4}
                      className={`w-full p-3 border rounded text-sm font-mono ${
                        darkMode ? 'bg-[hsl(var(--ds-muted))] border-[hsl(var(--ds-border))] text-white' : 'bg-[hsl(var(--ds-surface))] border-[hsl(var(--ds-border))]'
                      }`}
                    />
                  </div>
  
                  {/* Formula Actions */}
                  <div className="flex gap-2">
                    <DsButton type="button" size="sm" variant="outline" onClick={verifyFormula}>
                        Verify Formula
                      </DsButton>
                    <DsButton type="button" size="sm" variant="primary" onClick={saveFormula}>
                        Save Formula
                      </DsButton>
                  </div>
  
                  {/* Verification Result */}
                  {verificationResult && (
                    <div className={`mt-3 p-3 rounded text-sm ${
                      verificationResult.valid 
                        ? (darkMode ? 'bg-green-900 text-green-200' : 'bg-green-100 text-green-800')
                        : (darkMode ? 'bg-red-900 text-red-200' : 'bg-red-100 text-red-800')
                    }`}>
                      {verificationResult.valid ? verificationResult.message : verificationResult.error}
                    </div>
                  )}
                </div>
  
                {/* Right Side - Saved Formulas List */}
                <div className={`p-4 rounded-lg border ${darkMode ? 'border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-muted))]' : 'border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-muted))]'}`}>
                  <h4 className={`text-base font-semibold mb-3 ${darkMode ? 'text-white' : 'text-[hsl(var(--ds-fg))]'}`}>
                    Saved Formulas
                  </h4>
                  
                  <div className="space-y-3 max-h-96 overflow-y-auto">
                    {savedFormulas.length === 0 ? (
                      <div className={`text-center py-8 ${darkMode ? 'text-[hsl(var(--ds-muted-fg))]' : 'text-[hsl(var(--ds-muted-fg))]'}`}>
                        No formulas saved yet. Create your first formula using the builder.
                      </div>
                    ) : (
                      savedFormulas.map(formula => (
                        <div
                          key={formula.id}
                          className={`p-3 rounded-lg border cursor-pointer transition-colors ${
                            selectedFormula?.id === formula.id
                              ? (darkMode ? 'border-[hsl(var(--ds-primary))] bg-[hsl(var(--ds-secondary))]' : 'border-[hsl(var(--ds-primary))] bg-[hsl(var(--ds-accent))]')
                              : (darkMode ? 'border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-muted))] hover:bg-[hsl(var(--ds-muted))]' : 'border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] hover:bg-[hsl(var(--ds-muted))]')
                          }`}
                          onClick={() => setSelectedFormula(formula)}
                        >
                          <div className="flex justify-between items-start">
                            <div className="flex-1">
                              <h5 className={`font-medium text-sm ${darkMode ? 'text-white' : 'text-[hsl(var(--ds-fg))]'}`}>
                                {formula.name}
                              </h5>
                              <p className={`text-xs ${darkMode ? 'text-[hsl(var(--ds-muted-fg))]' : 'text-[hsl(var(--ds-muted-fg))]'}`}>
                                Code: {formula.code}
                              </p>
                              <p className={`text-xs font-mono mt-1 ${darkMode ? 'text-[hsl(var(--ds-muted-fg))]' : 'text-[hsl(var(--ds-fg))]'}`}>
                                {formula.formula}
                              </p>
                            </div>
                            <div className="flex gap-1">
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setSelectedFormula(formula);
                                }}
                                className={`p-1 rounded text-xs ${
                                  darkMode ? 'text-[hsl(var(--ds-accent-fg))] hover:bg-[hsl(var(--ds-muted))]' : 'text-[hsl(var(--ds-secondary))] hover:bg-[hsl(var(--ds-muted))]'
                                }`}
                              >
                                Edit
                              </button>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  deleteFormula(formula.id);
                                }}
                                className={`p-1 rounded text-xs ${
                                  darkMode ? 'text-red-400 hover:bg-[hsl(var(--ds-muted))]' : 'text-red-600 hover:bg-[hsl(var(--ds-muted))]'
                                }`}
                              >
                                Delete
                              </button>
                            </div>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

export default EnhancedSalaryStructureManager;